import {
  Completion,
  CompletionContext,
  CompletionResult,
  CompletionSource,
  insertCompletionText,
} from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import { EditorView } from '@codemirror/view';

import {
  completionPath,
  Identifier,
} from 'dashboard/components-lib/CodeEditor/completionSources/javascript/utils';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

export type ComputeFieldAutocompletionSchema = {
  [keyword: string]: FieldSchema | ComputeFieldAutocompletionSchema;
};

const quote = "'";

export default function casesCompletionSource(
  autocompletionSchema: ComputeFieldAutocompletionSchema,
) {
  const schema = transformSchema(autocompletionSchema);
  return (context: CompletionContext) => {
    const completionResult = scopeCompletionSource(schema)(context) as CompletionResult | null;
    if (!completionResult) {
      return null;
    }

    const inner = syntaxTree(context.state).resolveInner(context.pos, -1);
    const outer = syntaxTree(context.state).resolveInner(context.pos, 1);

    return {
      ...completionResult,
      options: completionResult.options.map((option) => {
        return {
          ...option,
          apply: (view: EditorView, completion: Completion, from: number, to: number) => {
            if (inner.name === 'VariableName') {
              view.dispatch(insertCompletionText(view.state, completion.label, from, to));
              return;
            }
            if (inner.name === '.') {
              if (completion.label.includes('-')) {
                view.dispatch(
                  insertCompletionText(
                    view.state,
                    `[${quote}${completion.label}${quote}]`,
                    from - 1,
                    to,
                  ),
                );
                return;
              } else {
                view.dispatch(
                  insertCompletionText(view.state, `.${completion.label}`, from - 1, to),
                );
                return;
              }
            } else if (inner.name === '[') {
              if (outer.name === ']') {
                view.dispatch(
                  insertCompletionText(
                    view.state,
                    `[${quote}${completion.label}${quote}]`,
                    from,
                    to + 1,
                  ),
                );
              } else {
                view.dispatch(
                  insertCompletionText(
                    view.state,
                    `${quote}${completion.label}${quote}]`,
                    from,
                    to,
                  ),
                );
              }
            }
          },
        };
      }),
    };
  };
}

type ScopeObject = { [key: string]: ScopeObject };

function scopeCompletionSource(scope: ScopeObject): CompletionSource {
  const cache: Map<ScopeObject, readonly Completion[]> = new Map();
  return (context: CompletionContext) => {
    const path = completionPath(context); // ex. {path: ["caseDataManager", "case"], name: "resol"}
    if (!path) return null;
    let target = scope;
    for (const step of path.path) {
      // remove extra quotes
      const cleanStep = step.replace(/\\"|"/g, '');
      target = target[cleanStep];
      if (target == null) return null;
    }

    let options = cache.get(target);
    if (!options) cache.set(target, (options = enumeratePropertyCompletions(target)));
    return {
      from: context.pos - path.name.length,
      options,
      validFor: Identifier,
    };
  };
}

function enumeratePropertyCompletions(obj: ScopeObject): readonly Completion[] {
  const options = [];

  if (typeof obj == 'object') {
    const seen: Set<string> = new Set();
    if (obj.config && obj.config['json-schema'] && obj.config['json-schema'].properties) {
      obj = obj.config['json-schema'].properties;
    }
    for (const name of Object.keys(obj)) {
      if (seen.has(name)) continue;
      if (name === 'key' || name === 'default-value' || name === 'label') continue;

      seen.add(name);
      let value;
      try {
        value = obj[name];
      } catch (_) {
        continue;
      }
      let type;
      if (typeof value == 'object') {
        type = 'object';
      } else {
        type = value;
      }
      options.push({
        label: name,
        info: renderCompletionInfo(type, obj, name),
        type: type,
      });
    }
  } else if (obj === 'string') {
    const propertyNames = Object.getOwnPropertyNames(String.prototype);
    for (const name of propertyNames) {
      options.push({
        label: name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        type: typeof String.prototype[name],
      });
    }
  } else if (obj === 'number') {
    const propertyNames = Object.getOwnPropertyNames(Number.prototype);
    for (const name of propertyNames) {
      options.push({
        label: name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        type: typeof Number.prototype[name],
      });
    }
  } else if (obj === 'boolean') {
    const propertyNames = Object.getOwnPropertyNames(Boolean.prototype);
    for (const name of propertyNames) {
      options.push({
        label: name,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        type: typeof Boolean.prototype[name],
      });
    }
  }
  return options;
}

type TargetDefinition = {
  [name: string]: ScopeObject;
};

function renderCompletionInfo(type: string, target: TargetDefinition, key: string) {
  return (_completion: Completion) => {
    if (!['object'].includes(type)) {
      return null;
    }

    const div = document.createElement('div');
    div.style.whiteSpace = 'pre';
    div.style.fontFamily = '"DM Mono", monospace';
    div.style.fontSize = '12px';
    div.style.lineHeight = '16.8px';
    div.style.maxHeight = '160px';
    div.style.overflowY = 'scroll';

    // This is a hacky but quick way to get a nice shape for this.
    // TODO: Replace with an actual parser / formatter version of this.
    if (type === 'object') {
      const json = `${key}: ${JSON.stringify(target[key], null, 4)}`;
      const unquoted = json
        // Handle "key": "type"
        .replace(
          /"([^"]+)": "([^"]+)"/g,
          '$1: <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // Handle key: "type"
        .replace(
          /([^"]+): "([^"]+)"/g,
          '$1: <span class="cm-persona-completionInfo-type">$2</span>',
        )
        // Handle "key": {
        .replace(/"([^"]+)": {/g, '$1: {');
      div.innerHTML = unquoted;
    }

    return div;
  };
}

export const transformSchema = (
  target: ComputeFieldAutocompletionSchema | FieldSchema | null,
  // eslint-disable-next-line
): any => {
  if (typeof target !== 'object' || target == null) {
    return target;
  }

  let jsonSchema;
  if (target.type) {
    switch (target.type) {
      case FieldSchemaType.String:
      case 'string':
        return 'string';
      case FieldSchemaType.Number:
      case 'number':
        return 'number';
      case FieldSchemaType.Choices:
        return 'enum';
      case FieldSchemaType.Boolean:
      case 'boolean':
        return 'boolean';
      case FieldSchemaType.Json:
        jsonSchema =
          typeof target.config?.['json-schema'] === 'string'
            ? JSON.parse(target.config?.['json-schema'])
            : target.config?.['json-schema'];

        /** Note (gary johnson): Here we are trying to guess the type of the
         * json schema. The json schema is typed as a string to give us the
         * ability to write complete json schema in the config. But we need to
         * know the type of the json schema to be able to provide the right
         * autocompletion.**/
        if (jsonSchema?.properties) {
          return transformSchema(jsonSchema?.properties);
        }

        if (jsonSchema?.anyOf) {
          const firstItem = jsonSchema?.anyOf?.[0];
          return firstItem.enum ? typeof firstItem.enum[0] : 'object';
        }

        return 'object';
      case FieldSchemaType.Hash:
      case 'hash':
        return 'object';
      case FieldSchemaType.Array:
        return 'array';
      case FieldSchemaType.MultiChoices:
        return typeof target.config.options?.[0] === 'string' ? 'string' : 'number';
      default:
        return target;
    }
  } else {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!target.type && !target.fieldType && target['template-filters']) {
      return 'string';
    }
  }

  const result = Array.isArray(target) ? [] : {};
  for (const key of Object.keys(target)) {
    if (['metadata'].includes(key)) continue;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    result[key] = transformSchema(target[key]);
  }
  return result;
};
